import React, { useState, useRef } from 'react';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, 
  PieChart, Pie, Cell, ScatterChart, Scatter, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  FileSpreadsheet, Upload, Clipboard, Play, AlertCircle, 
  CheckCircle, HelpCircle, TrendingUp, Cpu, Compass, 
  Layers, ChevronRight, BarChart2, FileText, Sparkles, Loader2, ArrowRight
} from 'lucide-react';
import { DataAnalysisResult, ChartSuggestion, ReportTemplate, ResearchReport, ResearchSession } from '../types';

interface DataAnalyzerProps {
  activeTemplate: ReportTemplate;
  onReportGenerated: (report: ResearchReport, query: string) => void;
  language: 'uk' | 'en' | 'pl';
}

// Highly representative sample datasets for a seamless out-of-the-box experience
const SAMPLE_DATASETS = [
  {
    name: 'Tesla Deliveries & Financials (2020-2025)',
    headers: ['Year', 'Deliveries_K', 'Revenue_B', 'NetIncome_B', 'StockPrice'],
    data: [
      { Year: '2020', Deliveries_K: 499, Revenue_B: 31.5, NetIncome_B: 0.7, StockPrice: 235 },
      { Year: '2021', Deliveries_K: 936, Revenue_B: 53.8, NetIncome_B: 5.6, StockPrice: 352 },
      { Year: '2022', Deliveries_K: 1313, Revenue_B: 81.4, NetIncome_B: 12.6, StockPrice: 207 },
      { Year: '2023', Deliveries_K: 1808, Revenue_B: 96.7, NetIncome_B: 15.0, StockPrice: 248 },
      { Year: '2024', Deliveries_K: 1840, Revenue_B: 98.2, NetIncome_B: 13.4, StockPrice: 195 },
      { Year: '2025', Deliveries_K: 2150, Revenue_B: 114.5, NetIncome_B: 16.2, StockPrice: 280 }
    ]
  },
  {
    name: 'Global AI Chip Market Shares',
    headers: ['Company', 'MarketShare_Percent', 'Revenue_B', 'GrowthRate_Percent'],
    data: [
      { Company: 'Nvidia', MarketShare_Percent: 78, Revenue_B: 84.2, GrowthRate_Percent: 125 },
      { Company: 'AMD', MarketShare_Percent: 10, Revenue_B: 11.5, GrowthRate_Percent: 45 },
      { Company: 'Intel', MarketShare_Percent: 5, Revenue_B: 5.4, GrowthRate_Percent: -12 },
      { Company: 'Broadcom', MarketShare_Percent: 4, Revenue_B: 4.8, GrowthRate_Percent: 32 },
      { Company: 'Google (TPU)', MarketShare_Percent: 2, Revenue_B: 2.1, GrowthRate_Percent: 80 },
      { Company: 'Others', MarketShare_Percent: 1, Revenue_B: 1.1, GrowthRate_Percent: 15 }
    ]
  },
  {
    name: 'Infrastructure Server Load & Anomalies',
    headers: ['Hour', 'RequestsPerSec', 'CPULoad_Percent', 'ErrorRate_Percent', 'Latency_ms'],
    data: [
      { Hour: '00:00', RequestsPerSec: 1200, CPULoad_Percent: 15, ErrorRate_Percent: 0.01, Latency_ms: 45 },
      { Hour: '04:00', RequestsPerSec: 450, CPULoad_Percent: 8, ErrorRate_Percent: 0.01, Latency_ms: 30 },
      { Hour: '08:00', RequestsPerSec: 5400, CPULoad_Percent: 64, ErrorRate_Percent: 0.05, Latency_ms: 85 },
      { Hour: '12:00', RequestsPerSec: 9200, CPULoad_Percent: 94, ErrorRate_Percent: 4.82, Latency_ms: 420 }, // Anomaly!
      { Hour: '16:00', RequestsPerSec: 6800, CPULoad_Percent: 72, ErrorRate_Percent: 0.12, Latency_ms: 110 },
      { Hour: '20:00', RequestsPerSec: 4100, CPULoad_Percent: 48, ErrorRate_Percent: 0.02, Latency_ms: 65 }
    ]
  }
];

export default function DataAnalyzer({ activeTemplate, onReportGenerated, language }: DataAnalyzerProps) {
  // Input states
  const [inputText, setInputText] = useState('');
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [parserError, setParserError] = useState<string | null>(null);

  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<DataAnalysisResult | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Chart view states
  const [selectedChart, setSelectedChart] = useState<ChartSuggestion | null>(null);

  // Report generation states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse CSV helper
  const parseCSV = (text: string) => {
    try {
      const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (lines.length < 2) {
        throw new Error(language === 'uk' ? 'Файл має містити щонайменше рядок заголовків та один рядок даних.' : 'File must contain at least a header row and one data row.');
      }

      // Detect separator (comma, semicolon, or tab)
      const firstLine = lines[0];
      let separator = ',';
      if (firstLine.includes('\t')) separator = '\t';
      else if (firstLine.includes(';')) separator = ';';

      // Parse headers
      const parsedHeaders = firstLine.split(separator).map(h => h.replace(/^["']|["']$/g, '').trim());
      
      // Parse rows
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const columns = lines[i].split(separator).map(c => c.replace(/^["']|["']$/g, '').trim());
        const rowObj: any = {};
        
        parsedHeaders.forEach((header, index) => {
          let val: any = columns[index] || '';
          // Attempt float parse
          if (val !== '' && !isNaN(val as any)) {
            val = Number(val);
          }
          rowObj[header] = val;
        });
        rows.push(rowObj);
      }

      setHeaders(parsedHeaders);
      setParsedData(rows);
      setParserError(null);
      setAnalysisResult(null);
      setSelectedChart(null);
    } catch (err: any) {
      setParserError(err.message || 'Failed to parse CSV.');
      setParsedData(null);
    }
  };

  // Parse JSON helper
  const parseJSON = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      let rows: any[] = [];
      
      if (Array.isArray(parsed)) {
        rows = parsed;
      } else if (parsed && typeof parsed === 'object') {
        // Try to find an array inside object
        const arrays = Object.values(parsed).filter(v => Array.isArray(v)) as any[][];
        if (arrays.length > 0) {
          rows = arrays[0];
        } else {
          rows = [parsed];
        }
      }

      if (rows.length === 0) {
        throw new Error(language === 'uk' ? 'Не знайдено масивів даних у наданому JSON.' : 'No data arrays found in JSON.');
      }

      const parsedHeaders = Object.keys(rows[0]);
      setHeaders(parsedHeaders);
      setParsedData(rows);
      setParserError(null);
      setAnalysisResult(null);
      setSelectedChart(null);
    } catch (err: any) {
      setParserError(err.message || 'Failed to parse JSON.');
      setParsedData(null);
    }
  };

  const handleTextSubmit = () => {
    const trimmed = inputText.trim();
    if (!trimmed) return;
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      parseJSON(trimmed);
    } else {
      parseCSV(trimmed);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setInputText(text);
      if (file.name.endsWith('.json')) {
        parseJSON(text);
      } else {
        parseCSV(text);
      }
    };
    reader.readAsText(file);
  };

  const loadSampleDataset = (dataset: typeof SAMPLE_DATASETS[0]) => {
    setHeaders(dataset.headers);
    setParsedData(dataset.data);
    setInputText(JSON.stringify(dataset.data, null, 2));
    setParserError(null);
    setAnalysisResult(null);
    setSelectedChart(null);
  };

  // Run AI Data Analysis
  const handleAnalyzeData = async () => {
    if (!parsedData || isAnalyzing) return;
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const reqHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (customKey) {
        reqHeaders['x-api-key'] = customKey;
      }

      const response = await fetch('/api/analyze-data', {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({
          data: parsedData,
          headers,
          language
        })
      });

      if (!response.ok) {
        throw new Error(language === 'uk' ? 'Помилка на сервері під час аналізу даних.' : 'Server failed to analyze the data.');
      }

      const result = await response.json() as DataAnalysisResult;
      setAnalysisResult(result);
      
      // Auto-select first recommended chart if available
      if (result.chartSuggestions && result.chartSuggestions.length > 0) {
        setSelectedChart(result.chartSuggestions[0]);
      }
    } catch (err: any) {
      setAnalysisError(err.message || 'Error occurred.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Run AI Report Synthesis based on data & custom template
  const handleGenerateReportFromData = async () => {
    if (!parsedData || !analysisResult || isGeneratingReport) return;
    setIsGeneratingReport(true);
    setReportError(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const reqHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (customKey) {
        reqHeaders['x-api-key'] = customKey;
      }

      const response = await fetch('/api/generate-data-report', {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({
          data: parsedData,
          analysisResult,
          customTemplate: activeTemplate,
          language
        })
      });

      if (!response.ok) {
        throw new Error('Failed to synthesize data report.');
      }

      const finalReport = await response.json() as ResearchReport;
      
      // Send the generated report up to App.tsx to load in the viewer
      onReportGenerated(
        finalReport, 
        language === 'uk' 
          ? `Інтелектуальний звіт за набором даних: ${activeTemplate.name}` 
          : `Intellectual Report on dataset: ${activeTemplate.name}`
      );
    } catch (err: any) {
      setReportError(err.message || 'Failed to generate report.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Convert chart values to numbers so Recharts does not glitch
  const prepareChartData = (rawRows: any[], xKey: string, yKey: string, secKey?: string) => {
    return rawRows.map(row => {
      const cleaned = { ...row };
      
      // Format X value if string, keep clean
      if (typeof cleaned[xKey] === 'number') {
        cleaned[xKey] = String(cleaned[xKey]);
      }

      // Parse primary Y Key
      if (yKey && cleaned[yKey] !== undefined) {
        const val = String(cleaned[yKey]).replace(/[^0-9.-]/g, '');
        const floatVal = parseFloat(val);
        cleaned[yKey] = !isNaN(floatVal) ? floatVal : 0;
      }

      // Parse secondary Y Key
      if (secKey && cleaned[secKey] !== undefined) {
        const val = String(cleaned[secKey]).replace(/[^0-9.-]/g, '');
        const floatVal = parseFloat(val);
        cleaned[secKey] = !isNaN(floatVal) ? floatVal : 0;
      }

      return cleaned;
    });
  };

  // Theme colors mapping
  const activeColor = 
    activeTemplate.styleAccent === 'cyan' ? '#06b6d4' :
    activeTemplate.styleAccent === 'emerald' ? '#10b981' :
    activeTemplate.styleAccent === 'purple' ? '#a855f7' :
    activeTemplate.styleAccent === 'amber' ? '#f59e0b' : '#f43f5e';

  const secondaryColor = '#64748b';

  return (
    <div className="space-y-6">
      
      {/* Step 1: Input Dataset */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 relative shadow-xs">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-red-500/[0.03] rounded-full blur-3xl pointer-events-none" />

        <h2 className="text-sm font-bold font-mono text-slate-800 flex items-center gap-2 mb-2 uppercase">
          <FileSpreadsheet className="w-4 h-4 text-red-500" />
          {language === 'uk' ? '1. ЗАВАНТАЖЕННЯ ТА ПОПЕРЕДНІЙ АНАЛІЗ ДАНИХ' : '1. DATASET PORTAL'}
        </h2>
        <p className="text-[11px] text-slate-500 mb-4 font-mono leading-relaxed">
          {language === 'uk'
            ? 'Завантажте файл (CSV, JSON), вставте скопійовану таблицю з Excel або скористайтеся готовими демонстраційними наборами даних.'
            : 'Upload structural files (CSV, JSON), paste rows from Excel spreadsheets, or use a sample dataset below.'}
        </p>

        {/* Demo Samples */}
        <div className="mb-4 space-y-2">
          <button
            type="button"
            onClick={() => loadSampleDataset(SAMPLE_DATASETS[0])}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{language === 'uk' ? "⚡ Спробувати на демо-прикладі в 1 клік (Аналіз продажів та маржі 2026)" : "⚡ Try 1-click Demo Sample (Sales & Margin Analysis 2026)"}</span>
          </button>

          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 font-mono">
            {language === 'uk' ? 'ІНШІ ДЕМО-НАБОРИ ДАНИХ:' : 'OTHER DEMO SAMPLES:'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {SAMPLE_DATASETS.map((demo, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => loadSampleDataset(demo)}
                className="text-left p-2.5 rounded border border-slate-200 bg-slate-50 hover:border-red-500/30 text-slate-650 hover:text-slate-800 transition-all text-[10px] font-mono cursor-pointer"
              >
                <p className="font-bold text-slate-700 line-clamp-1">{demo.name}</p>
                <p className="text-[8px] text-slate-400 mt-1 uppercase">
                  {demo.headers.length} COLUMNS | {demo.data.length} ROWS
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Form */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Left box: Paste Area */}
            <div className="md:col-span-2 space-y-1.5">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest font-mono">
                {language === 'uk' ? 'ВСТАВИТИ СТРУКТУРОВАНИЙ ТЕКСТ:' : 'PASTE SPREADSHEET ROWS:'}
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={
                  language === 'uk' 
                    ? "Ім'я,Показник_А,Значення\nКатегорія1,45,200\nКатегорія2,68,230" 
                    : "Year,Sales,Profit\n2023,150,45\n2024,210,68"
                }
                rows={4}
                className="w-full text-[10px] bg-slate-50 text-slate-700 p-3 rounded border border-slate-200 font-mono focus:outline-none focus:border-red-500 resize-none leading-relaxed"
              />
            </div>

            {/* Right box: File drag drop */}
            <div className="flex flex-col justify-between p-4 bg-slate-50 rounded border border-slate-200 hover:border-slate-350 transition-colors">
              <div className="space-y-1.5 text-center py-2">
                <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                <p className="text-[10px] font-bold font-mono text-slate-805 uppercase">
                  {language === 'uk' ? 'ЗАВАНТАЖИТИ ФАЙЛ' : 'UPLOAD CSV/JSON'}
                </p>
                <p className="text-[8px] text-slate-500 font-mono">
                  CSV, JSON, TSV
                </p>
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv,.json,.tsv,.txt"
                className="hidden"
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-1.5 bg-white border border-slate-200 text-[10px] font-mono font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded cursor-pointer transition-colors shadow-xs"
              >
                {language === 'uk' ? 'ОГЛЯД...' : 'BROWSE...'}
              </button>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div>
              {parserError && (
                <div className="flex items-center gap-1.5 text-red-600 text-[10px] font-mono">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>{parserError}</span>
                </div>
              )}
              {parsedData && !parserError && (
                <div className="flex items-center gap-1.5 text-emerald-600 text-[10px] font-mono">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                  <span>
                    {language === 'uk' 
                      ? `Імпортовано: ${headers.length} колонок, ${parsedData.length} рядків.` 
                      : `Imported successfully: ${headers.length} cols, ${parsedData.length} rows.`}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {inputText && (
                <button
                  type="button"
                  onClick={handleTextSubmit}
                  className="px-4 py-1.5 bg-white border border-slate-200 text-slate-650 font-bold rounded text-[10px] font-mono uppercase cursor-pointer hover:bg-slate-50 transition-colors animate-fade-in"
                >
                  {language === 'uk' ? 'ОБРОБИТИ ВВЕДЕНЕ' : 'PARSE RAW INPUT'}
                </button>
              )}
              <button
                type="button"
                disabled={!parsedData || isAnalyzing}
                onClick={handleAnalyzeData}
                className="px-5 py-1.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold rounded text-[10px] uppercase font-mono tracking-wider transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{language === 'uk' ? 'АНАЛІЗ...' : 'ANALYZING...'}</span>
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5 fill-white" />
                    <span>{language === 'uk' ? 'ЗАПУСТИТИ АНАЛІЗ ШІ' : 'RUN AI ANALYSIS'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Data table preview */}
        {parsedData && (
          <div className="mt-5 border-t border-slate-200 pt-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 font-mono">
              {language === 'uk' ? 'ПОПЕРЕДНІЙ ПЕРЕГЛЯД ДАНИХ (ПЕРШІ 6 РЯДКІВ):' : 'PREVIEW INGESTED MATRIX (FIRST 6 ROWS):'}
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-left border-collapse font-mono text-[10px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {headers.map(h => (
                      <th key={h} className="p-2 text-slate-650 font-bold uppercase tracking-tight">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 6).map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-200 bg-white hover:bg-slate-50">
                      {headers.map(h => (
                        <td key={h} className="p-2 text-slate-700 truncate max-w-[150px]" title={String(row[h])}>
                          {String(row[h])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: AI Neural Insights & Anomaly Detection */}
      {analysisResult && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Neural Diagnostics (Anomalies, Correlations, Trends) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Dataset Digest Summary */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 relative shadow-xs">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-red-500/[0.03] rounded-full blur-2xl pointer-events-none" />
              <h3 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 mb-3 uppercase">
                <Cpu className="w-4 h-4 text-red-500" />
                {language === 'uk' ? 'НЕЙРОМЕРЕЖЕВИЙ АНАЛІТИЧНИЙ ДАЙДЖЕСТ' : 'NEURAL ANALYSIS DIGEST'}
              </h3>
              <div className="p-4 bg-slate-50 rounded border border-slate-200 text-[11px] font-mono text-slate-700 leading-relaxed whitespace-pre-wrap">
                {analysisResult.summary}
              </div>
            </div>

            {/* Trends and Correlations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* Trends Card */}
              <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <h4 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 mb-3.5 uppercase border-b border-slate-200 pb-2">
                    <TrendingUp className="w-4 h-4 text-red-500" />
                    {language === 'uk' ? 'ВИЯВЛЕНІ ТРЕНДИ' : 'DISCOVERED TRENDS'}
                  </h4>
                  <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                    {analysisResult.trends.map((t, idx) => (
                      <div key={idx} className="p-2.5 rounded bg-slate-50 border border-slate-200 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-800 font-mono uppercase">{t.metric}</span>
                          <span className={`text-[8px] font-bold uppercase px-1 rounded ${
                            t.direction === 'upward' ? 'bg-green-500/10 text-green-600' :
                            t.direction === 'downward' ? 'bg-rose-500/10 text-rose-600' :
                            t.direction === 'volatile' ? 'bg-amber-500/10 text-amber-600' : 'bg-slate-500/10 text-slate-650'
                          }`}>
                            {t.direction}
                          </span>
                        </div>
                        <p className="text-[9px] text-slate-500 font-mono leading-relaxed">{t.description}</p>
                        <p className="text-[8px] text-red-500 font-mono italic">Significance: {t.significance}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Correlations Card */}
              <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <h4 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 mb-3.5 uppercase border-b border-slate-200 pb-2">
                    <Compass className="w-4 h-4 text-red-500" />
                    {language === 'uk' ? 'ВЗАЄМОЗВ\'ЯЗКИ ТА КОРЕЛЯЦІЇ' : 'CORRELATION DIAGNOSTICS'}
                  </h4>
                  <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                    {analysisResult.correlations.map((c, idx) => (
                      <div key={idx} className="p-2.5 rounded bg-slate-50 border border-slate-200 space-y-1">
                        <div className="flex items-center justify-between text-[10px] font-mono font-bold">
                          <span className="text-slate-700 truncate max-w-[120px]">{c.varA} ↔ {c.varB}</span>
                          <span className={`text-[9px] font-mono ${c.coefficient > 0 ? 'text-green-600' : 'text-rose-600'}`}>
                            r = {c.coefficient.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-[9px] text-slate-500 font-mono leading-relaxed">{c.explanation}</p>
                        <div className="flex justify-end">
                          <span className="bg-red-50 text-red-500 border border-red-100 text-[8px] px-1 rounded uppercase font-bold font-mono">
                            {c.strength} correlation
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>

            {/* Anomalies List */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-xs">
              <h3 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 mb-3.5 uppercase">
                <AlertCircle className="w-4 h-4 text-red-500" />
                {language === 'uk' ? 'ДЕТЕКТОР АНОМАЛІЙ ТА ВИКИДІВ' : 'ANOMALY & OUTLIER DETECTION'}
              </h3>
              
              {analysisResult.anomalies.length === 0 ? (
                <div className="p-4 bg-emerald-50 rounded border border-emerald-250 text-[10px] font-mono text-emerald-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>{language === 'uk' ? 'Аномалій або серйозних статистичних відхилень у наборі даних не виявлено.' : 'No statistically significant anomalies or outliers detected.'}</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {analysisResult.anomalies.map((an, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded border border-red-200 flex items-start gap-2.5">
                      <div className={`p-1.5 rounded text-red-500 ${
                        an.severity === 'high' ? 'bg-red-100' :
                        an.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <AlertCircle className="w-4 h-4" />
                      </div>
                      <div className="space-y-1 font-mono">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-slate-800 uppercase">{an.metric}</span>
                          <span className="text-[8px] bg-slate-100 px-1 border border-slate-200 rounded text-red-500">
                            {language === 'uk' ? `Рядок ${an.rowNumber}` : `Row ${an.rowNumber}`}
                          </span>
                        </div>
                        <p className="text-[9px] text-red-650 leading-relaxed">{an.explanation}</p>
                        <p className="text-[8px] text-slate-500">Value: <strong className="text-slate-700">{an.value}</strong></p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* AI Interactive Visualizer panel on the right */}
          <div className="space-y-6">
            
            {/* Visualization selector */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-xs">
              <h3 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 mb-1 uppercase">
                <BarChart2 className="w-4 h-4 text-red-500" />
                {language === 'uk' ? 'РЕКОМЕНДАЦІЇ ГРАФІКІВ ВІД ШІ' : 'AI CHART RECOMMENDATIONS'}
              </h3>
              <p className="text-[9px] text-slate-400 font-mono leading-relaxed">
                {language === 'uk'
                  ? 'ШІ проаналізував стовпці та рекомендує наступні інтерактивні візуалізації:'
                  : 'AI analyzed your variables and formulated these high-fidelity visual structures:'}
              </p>

              <div className="space-y-2">
                {analysisResult.chartSuggestions.map((sug, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedChart(sug)}
                    className={`w-full text-left p-3 rounded border text-xs font-mono transition-all flex items-start gap-3 cursor-pointer ${
                      selectedChart?.title === sug.title
                        ? 'bg-red-50/50 border-red-500/50 text-slate-800 font-bold'
                        : 'bg-slate-50 border-slate-200 text-slate-650 hover:border-slate-350 hover:text-slate-800'
                    }`}
                  >
                    <div className="bg-red-50 p-1.5 rounded self-center text-red-500 border border-red-100">
                      <BarChart2 className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold uppercase tracking-tight text-[10px] text-slate-800">{sug.title}</p>
                      <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-1">{sug.description}</p>
                      <p className="text-[8px] text-red-500 mt-1 uppercase">
                        Type: {sug.type} | X: {sug.xKey} | Y: {sug.yKey}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Document compilation block */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 relative overflow-hidden shadow-xs">
              <div className="absolute top-0 right-0 -mr-12 -mt-12 w-32 h-32 bg-red-500/[0.03] rounded-full blur-2xl pointer-events-none" />
              
              <h3 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 uppercase">
                <FileText className="w-4 h-4 text-red-500" />
                {language === 'uk' ? 'ГЕНЕРАТОР ПОВНИХ ЗВІТІВ' : 'AI REPORT COMPILER'}
              </h3>
              
              <p className="text-[9px] text-slate-450 font-mono leading-relaxed">
                {language === 'uk'
                  ? 'Згенеруйте детальний науково-дослідницький звіт на основі проаналізованих даних за допомогою активного шаблону:'
                  : 'Compile deep-dive diagnostic research reports containing introduction, methodology, and conclusion based on active template rules:'}
              </p>

              <div className="p-3 bg-slate-50 rounded border border-slate-200 font-mono space-y-1">
                <p className="text-[8px] text-slate-500 uppercase tracking-wider font-bold">
                  {language === 'uk' ? 'Активний шаблон звіту:' : 'Active Template Design:'}
                </p>
                <p className="text-[10px] text-slate-800 uppercase font-bold">{activeTemplate.name}</p>
                <p className="text-[8px] text-slate-500 truncate mt-0.5">{activeTemplate.description}</p>
              </div>

              {reportError && (
                <div className="text-[9px] text-red-650 font-mono bg-red-50 p-2 rounded border border-red-200">
                  {reportError}
                </div>
              )}

              <button
                type="button"
                disabled={isGeneratingReport}
                onClick={handleGenerateReportFromData}
                className="w-full py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold rounded text-[10px] font-mono uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{language === 'uk' ? 'СИНТЕЗ ЗВІТУ ШІ...' : 'SYNTHESIZING REPORT...'}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{language === 'uk' ? 'ЗГЕНЕРУВАТИ ЗВІТ ЗА ШАБЛОНОМ' : 'GENERATE CUSTOM REPORT'}</span>
                    <ArrowRight className="w-3 h-3 ml-0.5" />
                  </>
                )}
              </button>
            </div>

          </div>

        </div>
      )}

      {/* Selected Chart Rendering Portal */}
      {analysisResult && selectedChart && (
        <div id="recharts-visualizer-portal" className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 relative shadow-xs">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-red-500/[0.03] rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-4 mb-5 gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <h3 className="text-xs font-bold font-mono text-slate-800 uppercase tracking-wider">
                  {selectedChart.title}
                </h3>
              </div>
              <p className="text-[10px] text-slate-500 font-mono mt-1">
                {selectedChart.explanation}
              </p>
            </div>
            
            {/* Chart type badge */}
            <div className="px-3 py-1 bg-red-50 text-red-500 border border-red-200 text-[9px] font-mono rounded self-start sm:self-center uppercase font-bold">
              AI {selectedChart.type} CHART
            </div>
          </div>

          {/* Interactive Recharts Area */}
          <div className="h-72 w-full bg-slate-50 p-4 rounded border border-slate-200 relative">
            <ResponsiveContainer width="100%" height="100%">
              {selectedChart.type === 'line' ? (
                <LineChart data={prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey, selectedChart.secondaryYKey)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey={selectedChart.xKey} stroke="#64748b" fontSize={9} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={9} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', fontSize: 10, color: '#1e293b' }} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Line type="monotone" dataKey={selectedChart.yKey} stroke={activeColor} strokeWidth={2} dot={{ fill: activeColor, r: 4 }} activeDot={{ r: 6 }} />
                  {selectedChart.secondaryYKey && (
                    <Line type="monotone" dataKey={selectedChart.secondaryYKey} stroke={secondaryColor} strokeWidth={1.5} strokeDasharray="3 3" />
                  )}
                </LineChart>
              ) : selectedChart.type === 'bar' ? (
                <BarChart data={prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey, selectedChart.secondaryYKey)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey={selectedChart.xKey} stroke="#64748b" fontSize={9} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={9} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', fontSize: 10, color: '#1e293b' }} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Bar dataKey={selectedChart.yKey} fill={activeColor} radius={[3, 3, 0, 0]} />
                  {selectedChart.secondaryYKey && (
                    <Bar dataKey={selectedChart.secondaryYKey} fill={secondaryColor} radius={[3, 3, 0, 0]} />
                  )}
                </BarChart>
              ) : selectedChart.type === 'area' ? (
                <AreaChart data={prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey, selectedChart.secondaryYKey)}>
                  <defs>
                    <linearGradient id="colorY" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={activeColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={activeColor} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey={selectedChart.xKey} stroke="#64748b" fontSize={9} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={9} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', fontSize: 10, color: '#1e293b' }} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Area type="monotone" dataKey={selectedChart.yKey} stroke={activeColor} fillOpacity={1} fill="url(#colorY)" strokeWidth={2} />
                </AreaChart>
              ) : selectedChart.type === 'scatter' ? (
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid stroke="#e2e8f0" />
                  <XAxis type="number" dataKey={selectedChart.xKey} name={selectedChart.xKey} stroke="#64748b" fontSize={9} />
                  <YAxis type="number" dataKey={selectedChart.yKey} name={selectedChart.yKey} stroke="#64748b" fontSize={9} />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', fontSize: 10, color: '#1e293b' }} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Scatter name={selectedChart.title} data={prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey)} fill={activeColor} />
                </ScatterChart>
              ) : selectedChart.type === 'pie' ? (
                <PieChart>
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', fontSize: 10, color: '#1e293b' }} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Pie
                    data={prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey={selectedChart.yKey}
                    nameKey={selectedChart.xKey}
                  >
                    {(prepareChartData(parsedData || [], selectedChart.xKey, selectedChart.yKey)).map((entry, index) => {
                      const colorsList = [
                        activeColor, '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#64748b'
                      ];
                      return <Cell key={`cell-${index}`} fill={colorsList[index % colorsList.length]} />;
                    })}
                  </Pie>
                </PieChart>
              ) : (
                /* Heatmap Render: grid layout representation */
                <div className="flex items-center justify-center h-full text-xs font-mono text-slate-500">
                  <div className="space-y-4 text-center">
                    <p className="uppercase text-slate-655 font-bold">Interactive Heatmap Matrix</p>
                    <div className="grid grid-cols-6 gap-2 max-w-sm mx-auto">
                      {(parsedData || []).map((row, rIdx) => (
                        <div 
                          key={rIdx} 
                          style={{ backgroundColor: activeColor, opacity: 0.15 + (rIdx * 0.15) }}
                          className="h-8 w-8 rounded flex items-center justify-center text-[8px] text-slate-800 font-bold"
                          title={`Val: ${row[selectedChart.yKey] || 0}`}
                        >
                          {rIdx + 1}
                        </div>
                      ))}
                    </div>
                    <p className="text-[8px] text-slate-500">{`Axis X: ${selectedChart.xKey} | Cells scale color intensity by ${selectedChart.yKey}`}</p>
                  </div>
                </div>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

    </div>
  );
}
